/**
 * Marketing attribution helper.
 *
 * Captures the very first landing page UTM + click-ID params into sessionStorage
 * so that — even after the user clicks through to /auth and signs up — we still
 * know which campaign brought them in. Forwarded as part of the /api/auth/register
 * payload so the user document gets a permanent `attribution` blob.
 *
 * Designed to be a no-op on subsequent navigations (first-touch attribution).
 */

const KEY = 'jobsurf_attribution_v1';

const PARAM_KEYS = [
  'utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term',
  'ttclid', 'fbclid', 'gclid',
];

/** Read the current URL's tracking params + referrer + path and persist them.
 *  Only writes if no prior attribution exists in this session (first-touch). */
export function captureAttributionFromUrl() {
  try {
    const existing = sessionStorage.getItem(KEY);
    if (existing) return JSON.parse(existing);

    const params = new URLSearchParams(window.location.search);
    const hasAny = PARAM_KEYS.some((k) => params.get(k));
    // Always persist landing_path + referrer; only persist UTM block when present.
    const data = {
      landing_path: window.location.pathname || '/',
      referrer: document.referrer || '',
    };
    PARAM_KEYS.forEach((k) => {
      const v = params.get(k);
      if (v) data[k] = v.slice(0, 200);
    });
    // If we have NO utm params AND no referrer, skip storing so a later
    // direct visit with utm params still wins first-touch.
    if (!hasAny && !data.referrer) return null;
    sessionStorage.setItem(KEY, JSON.stringify(data));
    return data;
  } catch (_) {
    return null;
  }
}

/** Return the persisted attribution (or empty object). */
export function getAttribution() {
  try {
    const raw = sessionStorage.getItem(KEY);
    return raw ? JSON.parse(raw) : {};
  } catch (_) {
    return {};
  }
}

/** Clear attribution — called after successful register so the next person
 *  on a shared device doesn't inherit the previous user's UTMs. */
export function clearAttribution() {
  try { sessionStorage.removeItem(KEY); } catch (_) {}
}
